/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  message,
  Card,
  Modal,
  Input,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';

import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalChangeStatus from 'components/ModalChangeStatus';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import API from 'utils/api';
import { onError, onSuccess } from 'utils/handlers';
import { isMutable } from 'utils/estadosGlobales';
import { selectEstadosGlobales } from 'store/catalogos';

export const permissionConfigMascara = {
  permissionModel: 'configuraciondemascara',
};

const baseURI = 'configuraciones/configuraciones-de-mascaras/';

// eslint-disable-next-line react/prop-types
function ConfiguracionDeMascaras({ permission }) {
  const estadosGlobales = useSelector(selectEstadosGlobales);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [mutable, setMutable] = useState(true);
  const [filterEstadosGlobales, setFilterEstadosGlobales] = useState([]);
  const [tiposDeMascaras, setTiposDeMascaras] = useState([]);
  const [visibleAuthorize, setVisibleAuthorize] = useState(false);
  const [visibleCancel, setVisibleCancel] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await API.get(baseURI);
      setData(res.data);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const resTipos = await API.get('catalogos/tipos-de-mascaras/');
        setTiposDeMascaras(resTipos.data || []);
        await fetchData();
        setLoading(false);
      } catch (err) {
        onError(err, setLoading);
      }
    };
    fetchAll();
    return () => API.tryCancel;
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (estadosGlobales.length > 0) {
      setFilterEstadosGlobales(estadosGlobales.filter((e) => [1, 3, 4].includes(e.id)));
    }
  }, [estadosGlobales]);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);
      if (!selectedRowKeys.length) {
        const response = await API.post(baseURI, values);
        if (response?.status === 201) {
          onSuccess(response, 'Agregado correctamente');
          onCancel();
          await fetchData();
        }
      } else {
        const [key] = selectedRowKeys;
        const response = await API.put(`${baseURI}${key}/`, values);
        if (response?.status === 200) {
          onSuccess(response, 'Actualizado correctamente');
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const rules = {
    required: [
      {
        required: true,
        message: 'El campo es requerido',
      },
    ],
    maxOne: [
      {
        required: true,
        message: 'El campo es requerido',
      },
      {
        max: 1,
        message: 'Máximo 1 carácter',
      },
    ],
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setMutable(isMutable(record));
    setTimeout(() => form.setFieldsValue(record));
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = (authorized) => {
    setVisible(true);
    const [key] = selectedRowKeys;
    const match = authorized || data.find((e) => e.id === key);
    setMutable(isMutable(match));
    setTimeout(() => form.setFieldsValue(match));
  };

  const onClickDelete = () => {
    if (mutable) {
      setVisibleAlert(true);
    } else {
      message.info('No se pueden eliminar las Configuraciones autorizadas');
    }
  };

  const onAuthorizeStatus = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      const res = await API.patch(`${baseURI}${key}/`, { estados_globales: 4 });
      if (res?.status === 200) {
        setVisibleAuthorize(false);
        setMutable(false);
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onCancelStatus = async (values) => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      const res = await API.patch(`${baseURI}${key}/`, { ...values, estados_globales: 5 });
      if (res?.status === 200) {
        setVisibleCancel(false);
        setMutable(false);
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const response = await API.delete(`${baseURI}${key}/`);
        if (response?.status === 204) {
          onSuccess(response, 'Eliminado correctamente');
          onCancel();
          setVisibleAlert(false);
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 200,
      render: (val) => tiposDeMascaras.find((t) => t.id === val)?.nombre,
    },
    {
      titleText: 'Separador',
      dataIndex: 'separador',
      key: 'separador',
      width: 100,
    },
    {
      titleText: 'Máscara',
      dataIndex: 'mascara',
      key: 'mascara',
      width: 300,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 120,
      render: (val) => estadosGlobales.find((g) => g.id === val)?.descripcion,
    },
  ];

  const handleSubmit = () => {
    form.submit();
  };

  function Formulario() {
    const modal = !selectedRowKeys.length;
    const colProps = modal ? { span: 24 } : { xs: 24, sm: 24, md: 12 };
    return (
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        initialValues={{ estados_globales: 1 }}
      >
        <Row gutter={10}>
          <Col {...colProps}>
            <Form.Item
              name="tipo"
              rules={rules.required}
              label="Tipo de Máscara"
              hasFeedback
            >
              <Select
                dataSource={tiposDeMascaras}
                valueProp="id"
                render={(item) => item.nombre}
                disabled={!!selectedRowKeys.length}
              />
            </Form.Item>
          </Col>

          <Col {...colProps}>
            <Form.Item
              name="separador"
              rules={rules.maxOne}
              label="Separador"
              hasFeedback
            >
              <Input allowClear maxLength={1} />
            </Form.Item>
          </Col>

          <Col {...colProps}>
            <Form.Item
              name="mascara"
              rules={rules.required}
              label="Máscara"
              hasFeedback
            >
              <Input allowClear />
            </Form.Item>
          </Col>

          <Col {...colProps}>
            <Form.Item
              label="Estado"
              name="estados_globales"
              rules={rules.required}
            >
              <Select dataSource={filterEstadosGlobales} disabled />
            </Form.Item>
          </Col>

          <Form.Item hidden>
            <Button htmlType="submit" />
          </Form.Item>
        </Row>
      </Form>
    );
  }

  const getDisplayName = () => {
    const [key] = selectedRowKeys;
    const config = data.find((g) => g.id === key);
    const tipo = tiposDeMascaras.find((t) => t.id === config?.tipo);
    return tipo?.nombre || '';
  };

  const normalizeDataAutorized = (estados_globales) => {
    const [key] = selectedRowKeys;
    const normalized = [...data]
      .map((e) => (e.id === key ? { ...e, estados_globales } : e));
    setMutable(false);
    setData(normalized);
    const match = normalized.find((e) => e.id === key);
    onClickEdit(match, true);
  };

  return (
    <Row id="configuraciones-de-mascaras" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!(visible && selectedRowKeys.length) && (
          <>
            <Table
              cols={columns}
              data={data}
              permission={permission}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{
                onClickAdd,
                onClickEdit,
                onClickDelete,
              }}
              customActions={[
                {
                  icon: <CheckCircleOutlined />,
                  text: 'Autorizar',
                  onClick: () => setVisibleAuthorize(true),
                  disabled: !mutable,
                  permission: { model: 'configuraciondemascara', action: 'autorizar' },
                },
                {
                  icon: <CloseCircleOutlined />,
                  text: 'Cancelar',
                  onClick: () => setVisibleCancel(true),
                  disabled: !mutable,
                  permission: { model: 'configuraciondemascara', action: 'cancelar' },
                },
              ]}
            />
            <ModalChangeStatus
              open={visibleAuthorize}
              onSubmit={onAuthorizeStatus}
              onCancel={() => setVisibleAuthorize(false)}
              authorize
            />
            <ModalChangeStatus
              open={visibleCancel}
              onSubmit={onCancelStatus}
              onCancel={() => setVisibleCancel(false)}
            />
          </>
        )}
        {(visible && !!selectedRowKeys.length) && (
          <Card
            className="form-container"
            title="Editar Configuración de Máscara"
            extra={(
              <FormSubmitControls
                onFinish={() => handleSubmit()}
                onCancel={onCancel}
                mutable={mutable}
                loading={loading}
                baseURI={baseURI}
                callback={normalizeDataAutorized}
                selectedRowKeys={selectedRowKeys}
                allowAuthorize
                allowCancel
              />
            )}
            bordered={false}
          >
            <Formulario />
          </Card>
        )}
      </Spin>
      {!selectedRowKeys.length && (
        <Modal
          visible={visible && !selectedRowKeys.length}
          title={(
            <FormSubmitControls
              label="Agregar Configuración de Máscara"
              onFinish={() => handleSubmit()}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          onCancel={onCancel}
          footer={null}
          forceRender
        >
          <Formulario modal />
        </Modal>
      )}
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        open={visibleAlert}
        content={`Configuración de Máscara ${getDisplayName()}`}
        loading={loading}
      />
    </Row>
  );
}

export default ConfiguracionDeMascaras;
